// The first run (SPEC-redesign §5.9, D-j, ruling 11).
//
// There is no wizard. The screen reveals over about two seconds, the kreska
// takes one breath and `⇅` stays highlighted for the first three runs.
//
// **The reveal is visual only.** Every row and every mark is in the DOM and
// reachable by assistive technology from the first frame (D-j); the prototype
// leaves the axis genuinely empty for ~3.5 s, so a resident who wants to know
// whether their tram is late waits for an animation. Here nobody does, and
// `data-ready` is not gated on any of this (§6.5).
//
// This module owns no markup in the wheel, the kreska or the axis — it owns the
// *signal*. Three attributes on `<html>`, which S1, S2 and S3 key their own
// stylesheets off:
//
//   data-intro="reveal"  the reveal is running (about 2 s)
//   data-breath          the kreska's 24 px breath is due
//   data-hint="grip"     `⇅` is highlighted — the first three runs
//
// Nothing here writes a style, so no session's CSS file is touched by this one.
import { memory } from '../state/memory.ts';

/** The reveal, end to end (§5.9). The prototype takes ~3.5 s; the spec says two. */
const REVEAL_MS = 2000;
/** The prototype's breath: fired 500 ms in, reverted at 1300 ms, a 24 px nudge. */
const BREATH_AT_MS = 500;
const BREATH_MS = 800;
/** With reduced motion there is no reveal at all — one hint line instead (§5.9, §8). */
const HINT_MS = 5000;
/** `⇅` is highlighted for the first three runs (`memory.runs`). */
const GRIP_RUNS = 3;

/**
 * The owner's own words, set at `/ideate` (SPEC-viewfinder §5, ruling 4). No
 * `⟷` beside the map — that glyph is the radius slider's — and no radius
 * clause, because the slider is visible. Measured at 280,03 px against the
 * 350 px a 390 px phone leaves the strip, so it is ONE line and cannot cover
 * the first row under „teraz" (#236).
 */
const HINT_TEXT = '⇅ przeciągnij kreskę · przesuń mapę · ⓘ więcej';

const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

const root = () => document.documentElement;

/** Every timer this module has running, so a replay never races its predecessor. */
let timers: ReturnType<typeof setTimeout>[] = [];

function clearTimers() {
  for (const t of timers) clearTimeout(t);
  timers = [];
}

const after = (ms: number, fn: () => void) => {
  timers.push(setTimeout(fn, ms));
};

/**
 * The one-line hint that replaces the reveal under reduced motion. It is mounted
 * into the overlay host — this module's own ground — and placed under the kreska
 * by measurement rather than by reaching into another session's markup.
 */
function showHintStrip(host: HTMLElement) {
  const strip = document.createElement('p');
  strip.className = 'intro-hint';
  strip.textContent = HINT_TEXT;
  // A hint nobody asked for should not interrupt what is being read; it is
  // visible, not announced.
  strip.setAttribute('aria-hidden', 'true');
  host.append(strip);

  const kreska = document.getElementById('kreska');
  if (kreska) strip.style.top = `${Math.round(kreska.getBoundingClientRect().bottom)}px`;

  after(HINT_MS, () => {
    strip.remove();
  });
}

/**
 * Marks that end when the resident has done the thing they teach, rather than on
 * a clock: `⇅` after three runs. Set on every boot, reveal or no reveal.
 */
/**
 * `memory.runs` is read here **before** `src/main.ts` bumps it, so the first
 * ever boot reads 0 and the three runs that get the reveal are 0, 1 and 2.
 */
const firstRuns = () => memory.runs.get() < GRIP_RUNS;

function paintStandingHints() {
  if (firstRuns()) root().dataset.hint = 'grip';
  else delete root().dataset.hint;
}

function runReveal(host: HTMLElement) {
  clearTimers();
  if (reduced()) {
    // Nothing is lost with reduced motion, only the motion (§8). The reveal is
    // not shortened here, it never starts: this returns before `data-intro` is
    // set and before the breath is scheduled, so `circle-reveal` and
    // `axis-reveal` have nothing to key off. The one line the prototype shows in
    // its place stands instead.
    delete root().dataset.intro;
    showHintStrip(host);
    return;
  }
  root().dataset.intro = 'reveal';
  after(BREATH_AT_MS, () => {
    root().dataset.breath = 'true';
  });
  after(BREATH_AT_MS + BREATH_MS, () => {
    delete root().dataset.breath;
  });
  after(REVEAL_MS, () => {
    delete root().dataset.intro;
  });
}

/**
 * The first runs of this browser get the reveal; after that the app simply
 * appears. `memory.runs` is bumped by the boot itself (`src/main.ts`), and E2E
 * seeds it past the threshold through `seenIntro(page)` (ruling 11).
 */
export function startIntro(host: HTMLElement): void {
  paintStandingHints();
  if (!firstRuns()) return;
  runReveal(host);
}

/** „Pokaż jeszcze raz" in „Jak się poruszać": the same reveal, on demand. */
export function replayIntro(): void {
  const host = document.getElementById('overlay');
  if (host) runReveal(host);
}
